/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-magic-numbers */

import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { StateCreator } from 'zustand';

import { GetQuestionsResponse, GetStatsResponse } from '@/hooks/api/useCueingEngine';
import logger from '@/logger';
import { CueingEngineResult } from '@/pages/Journey/hooks/useStageCueingEngine';
import {
  AttemptInfo,
  EomModule,
  EomModuleStats,
  ExplanationStats,
  HintStats,
  NarrationStats,
  PreQuestionExplanationStats,
  QuestionType,
} from '@/pages/TaskPlayer/types';
import { getValidatedDataForCueingEngine } from '@/pages/TaskPlayer/validationUtil';
import { GetModuleListResponse } from '@/services/api';
import { AppState } from '@/slices/appSlice';
import { TestState } from '@/slices/testSlice';

const MAX_QUESTIONS_ALLOWED = 3;
const MIN_QUESTIONS_FOR_FAIL = 3;
const DEFAULT_PASS_THRESHOLD = 2 / 3;
const getPassThreshold = (isFlnMode: boolean) => (isFlnMode ? 0 : DEFAULT_PASS_THRESHOLD);
const getScoreRange = (questionStats: QuestionStats[], availableQuestions: QuestionType[]) => {
  const totalAsked = questionStats.length;
  const remainingCount = Math.min(MAX_QUESTIONS_ALLOWED - totalAsked, availableQuestions.length);
  const correctCount = questionStats.filter((stat) => stat.isCorrect).length;
  return {
    minScore: correctCount / (totalAsked + remainingCount),
    maxScore: (correctCount + remainingCount) / (totalAsked + remainingCount),
    score: correctCount / totalAsked,
    remainingCount,
  };
};

const selectRandomQuestion = (questions: QuestionType[]) => {
  return questions[Math.floor(Math.random() * questions.length)];
};

const isCurrentSetOver = (
  questionStats: QuestionStats[],
  availableQuestions: QuestionType[],
  isFlnMode: boolean,
) => {
  const { minScore, maxScore, remainingCount, score } = getScoreRange(
    questionStats,
    availableQuestions,
  );
  const passThreshold = getPassThreshold(isFlnMode);
  return {
    currentSetOver:
      remainingCount === 0 ||
      (!isFlnMode && (minScore >= passThreshold || maxScore < passThreshold)),
    score,
  };
};

const precompute = (
  getQuestionsResponse: GetQuestionsResponse,
  getStatsResponse: GetStatsResponse,
) => {
  return getValidatedDataForCueingEngine(getQuestionsResponse, getStatsResponse);
};

interface BasicQuestionStats {
  questionId: string;
  format: string;
  medium: string;
  seqNo: number;
  isCorrect: boolean;
  translationViewed: string[];
  type: number;
  cueCount: number;
  explanationStats: ExplanationStats[];
  hintStats: HintStats[];
  preQuestionExplanationStats?: PreQuestionExplanationStats;
  difficulty: number;
  narration?: NarrationStats;
}
interface QuestionStats extends BasicQuestionStats, AttemptInfo {
  attempt1?: AttemptInfo; // without any cues
  attempt2?: AttemptInfo; // for cue 1
  attempt3?: AttemptInfo; // for cue 2
}

export interface StageState {
  /** api response related state */
  moduleListResponse: GetModuleListResponse | undefined;
  getStatsResponse: GetStatsResponse | undefined;
  getQuestionsResponse: GetQuestionsResponse | undefined;
  difficultyWiseQuestions: { [difficulty: number]: QuestionType[] };
  minDifficulty: number;
  maxDifficulty: number;
  eomModuleMapping: {
    [evidenceId: string]: {
      [language: string]: EomModule;
    };
  };
  totalSets: number;
  cueingAttemptId: string | undefined;
  errors: string[]; // shared
  warnings: string[]; // shared

  /** stats related state */
  eomModuleStats: EomModuleStats[]; // shared
  explanationStats: ExplanationStats[]; // shared
  hintStats: HintStats[]; // shared
  translationViewed: string[]; // shared
  overallQuestionStats: QuestionStats[];

  /** stage journey related state */
  showStageTask: boolean;

  /** api execution related state */
  addStatsRetryCount: number;
  submitResultRetryCount: number;

  /** narration tracking (per question) */
  currentNarrationStats: { completed: boolean; playCount: number };

  /** question explanation viewed before attempt (cueing only) */
  currentPreQuestionExplanationStats?: PreQuestionExplanationStats;

  /** logic related state */
  currentModuleIndex: number | undefined; // shared
  question?: QuestionType; // shared
  isEvaluatingPivot: boolean;
  stageTaskResult: { status: 'PASS' | 'FAIL'; lastDifficulty: number; source: string } | undefined;
  currentSet: number;
  currentQuestionStat?: QuestionStats;
  currentDifficultyQuestionStats: QuestionStats[];
  skillDescription: string;
  setSkillDescription: (description: string) => void;

  /** api response parsing related behaviour */
  initialiseModules: ({
    moduleListResponse,
  }: {
    moduleListResponse: GetModuleListResponse;
  }) => void;
  initialiseCueing: ({
    getStatsResponse,
    getQuestionsResponse,
  }: {
    getStatsResponse: GetStatsResponse;
    getQuestionsResponse: GetQuestionsResponse;
  }) => void;

  /** narration related behaviour (Cueing / Learn) */
  resetCurrentNarrationStatsCueing: () => void;
  reportNarrationReached90Cueing: () => void;

  /** stats related behaviour */
  storeUserAttempt: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) => void;
  addTranslationViewed: (language: string) => void; // shared
  setEomModuleStats: (newStat: EomModuleStats) => void; // shared
  setExplanationStats: (newStat: ExplanationStats) => void; // shared
  setHintStats: (newStat: HintStats) => void; // shared
  setPreQuestionExplanationStats: (newStat: PreQuestionExplanationStats) => void;

  /** stage journey related behaviour */
  setShowStageTask: (show: boolean) => void;
  clearStageState: () => void;
  clearStageTaskResult: () => void;
  clearSharedState: () => void; // shared

  /** api execution related behaviour */
  incrementAddStatsRetryCount: () => void;
  incrementSubmitResultRetryCount: () => void;
  getSubmitResultRetryCount: () => number;
  getAddStatsRetryCount: () => number;

  /** logic related behaviour */
  setCurrentModuleIndex: (currentModuleIndex: number) => void;
  processUserAnswer: (source: string) => boolean;
  getCurrentDifficulty: () => number | undefined;
  getAvailableQuestions: () => QuestionType[];
  getCueingEngineResult: () => CueingEngineResult | undefined;
}

const getSharedInitialState = () => {
  return {
    errors: [], // shared
    warnings: [], // shared
    eomModuleStats: [], // shared
    explanationStats: [], // shared
    hintStats: [], // shared
    translationViewed: [], // shared
    currentModuleIndex: undefined, // shared
    question: undefined, // shared
  };
};

const getInitialState = () => {
  return {
    ...getSharedInitialState(),
    /** api response related state */
    moduleListResponse: undefined,
    getStatsResponse: undefined,
    getQuestionsResponse: undefined,
    difficultyWiseQuestions: {},
    minDifficulty: 1,
    maxDifficulty: 3,
    eomModuleMapping: {},
    totalSets: 1,
    cueingAttemptId: undefined,

    /** stats related state */
    overallQuestionStats: [],

    /** stage journey related state */
    showStageTask: false,

    /** api execution related state */
    addStatsRetryCount: 0,
    submitResultRetryCount: 0,

    /** narration tracking */
    currentNarrationStats: { completed: false, playCount: 0 },
    currentPreQuestionExplanationStats: undefined,

    /** logic related state */
    isEvaluatingPivot: false,
    stageTaskResult: undefined,
    currentSet: 0,
    currentQuestionStat: undefined,
    currentDifficultyQuestionStats: [],
  };
};

const getInititalTranslationViewed = (
  currentQuestion: QuestionType | undefined,
  mediumPriority: string[],
) => {
  if (currentQuestion && mediumPriority) {
    // eslint-disable-next-line unicorn/prefer-array-find
    const availableLanguages = mediumPriority.filter(
      (medium) => currentQuestion.mediumMapping[medium],
    );
    return [availableLanguages[0]];
  }
  return [];
};

export const createStageSlice: StateCreator<
  TestState & StageState & AppState,
  [['zustand/devtools', never]],
  [],
  StageState
> = (set, get) => ({
  ...getInitialState(),

  /** api response parsing related behaviour */
  initialiseModules: ({ moduleListResponse }) => {
    const { moduleList, lastCompletedModuleId } = moduleListResponse;
    const lastCompletedModuleIndex = lastCompletedModuleId
      ? moduleList.findIndex((module) => module.id === lastCompletedModuleId)
      : -1;
    if (lastCompletedModuleIndex < moduleList.length - 1) {
      set(
        {
          moduleListResponse,
          currentModuleIndex: lastCompletedModuleIndex + 1,
        },
        false,
        'stageSlice/initialiseModules',
      );
    } else {
      set(
        {
          showStageTask: true,
        },
        false,
        'stageSlice/initialiseModules',
      );
    }
  },
  initialiseCueing: ({
    getStatsResponse,
    getQuestionsResponse,
  }: {
    getStatsResponse: GetStatsResponse;
    getQuestionsResponse: GetQuestionsResponse;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const {
            difficultyWiseQuestions,
            minDifficulty,
            maxDifficulty,
            formativeQuestions,
            pivotQuestions,
            isValid,
            errors,
            totalSets,
          } = precompute(getQuestionsResponse, getStatsResponse);
          if (!isValid) {
            const info = [
              'Mode Learn ',
              'Level id ',
              _state.currentLevel,
              'Mission id ',
              _state.currentMission?.id,
              'Mission no ',
              _state.currentMission?.missionNo,
              'Stage id ',
              _state.currentStage?.id,
              'Stage no ',
              _state.currentStage?.stageNo,
            ];
            logger.info(info.join(' '));
          }
          if (isValid) {
            state.warnings = errors;
            state.getQuestionsResponse = {
              ...getQuestionsResponse,
              formativeQuestions,
              pivotQuestions,
            };
            state.getStatsResponse = getStatsResponse;
            state.eomModuleMapping = getQuestionsResponse.eomModuleMapping;
            state.totalSets = totalSets;
            state.cueingAttemptId = uuidv4();
            state.currentSet = 0;
            state.difficultyWiseQuestions = difficultyWiseQuestions;
            state.maxDifficulty = maxDifficulty;
            state.minDifficulty = minDifficulty || 1;
            const unaidedAccuracy =
              (getStatsResponse.unaidedAccuracyMetrics.unaidedCorrectAnswers || 0) /
              (getStatsResponse.unaidedAccuracyMetrics.totalFormativeQuestions || 1);
            if (
              getStatsResponse.studentStageDetails.formativeCompleted ||
              (unaidedAccuracy >= 0.8 && getStatsResponse.studentStageDetails.roundNumber === 1)
            ) {
              state.isEvaluatingPivot = true;
              state.currentSet = totalSets - 1;
              state.question = selectRandomQuestion(pivotQuestions);
            } else {
              const startingDifficulty =
                state.minDifficulty ||
                (getStatsResponse.studentStageDetails.lastDifficulty || 0) + 1;
              state.isEvaluatingPivot = false;
              state.question = selectRandomQuestion(difficultyWiseQuestions[startingDifficulty]);
            }
            state.translationViewed = getInititalTranslationViewed(
              state.question,
              getQuestionsResponse.mediumPriority,
            );
            state.overallQuestionStats = [];
            state.currentDifficultyQuestionStats = [];
            state.eomModuleStats = [];
            state.explanationStats = [];
            state.hintStats = [];
            state.currentNarrationStats = { completed: false, playCount: 0 };
          } else {
            state.errors = errors;
          }
        }),
      false,
      'stageSlice/initialiseCueing',
    ),

  /** stats related behaviour */
  storeUserAttempt: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          if (!state.currentDifficultyQuestionStats) {
            state.currentDifficultyQuestionStats = [];
          }
          if (!state.currentQuestionStat) {
            state.currentQuestionStat = {} as QuestionStats;
          }
          const {
            currentDifficultyQuestionStats,
            currentPreQuestionExplanationStats,
            currentQuestionStat,
            question,
            overallQuestionStats,
            translationViewed,
            isEvaluatingPivot,
            currentNarrationStats,
          } = state;
          const { answer, startTime, endTime, duration } = attemptInfo;
          if (!question) {
            return;
          }
          const voiceover = question.mediumMapping[medium]?.metadata?.voiceover;
          const narrationEnabled = voiceover?.type === 'audio' && !!voiceover?.value;
          const narration: NarrationStats = {
            enabled: narrationEnabled,
            completed: narrationEnabled ? currentNarrationStats.completed : false,
            playCount: narrationEnabled ? currentNarrationStats.playCount : 0,
          };
          currentQuestionStat.answer = answer;
          currentQuestionStat.medium = medium;
          currentQuestionStat.isCorrect = isCorrect;
          currentQuestionStat.duration = (currentQuestionStat.duration || 0) + duration;
          currentQuestionStat.endTime = endTime;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- narration is NarrationStats
          currentQuestionStat.narration = narration;
          const attemptKey = `attempt${hintUsed + 1}` as 'attempt1' | 'attempt2' | 'attempt3';
          if (!isEvaluatingPivot) {
            currentQuestionStat.cueCount = hintUsed;
            currentQuestionStat[attemptKey] = attemptInfo;
          }
          if (hintUsed === 0) {
            currentQuestionStat.questionId = question.id;
            currentQuestionStat.type = question.type;
            currentQuestionStat.format = question.format;
            currentQuestionStat.startTime = startTime;
            currentQuestionStat.seqNo =
              overallQuestionStats.length + currentDifficultyQuestionStats.length + 1;
            currentQuestionStat.translationViewed = translationViewed;
            currentQuestionStat.difficulty = question.difficulty;
            currentQuestionStat.preQuestionExplanationStats = currentPreQuestionExplanationStats;
            state.currentPreQuestionExplanationStats = undefined;
            currentDifficultyQuestionStats.push(currentQuestionStat);
          } else {
            const lastIndex = currentDifficultyQuestionStats.length - 1;
            if (lastIndex >= 0) {
              currentDifficultyQuestionStats[lastIndex] = {
                ...currentQuestionStat,
                hintStats: currentDifficultyQuestionStats[lastIndex]?.hintStats || [],
              };
            }
          }
        }),
      false,
      'stageSlice/storeUserAttempt',
    ),
  skillDescription: '',
  setSkillDescription: (description: string) => set({ skillDescription: description }),
  addTranslationViewed: (newLanguage: string) =>
    set(
      (state) => ({
        translationViewed: [...new Set([...state.translationViewed, newLanguage])],
      }),
      false,
      'stageSlice/addTranslationViewed',
    ),
  setEomModuleStats: (newStats: EomModuleStats[] | EomModuleStats) =>
    set(
      (state) => {
        if (Array.isArray(newStats)) {
          return { eomModuleStats: newStats };
        }

        const moduleKey = `${newStats.questionId}_${newStats.eomModuleId}`;
        const existingIndex = state.eomModuleStats.findIndex(
          (stat) => `${stat.questionId}_${stat.eomModuleId}` === moduleKey,
        );

        if (existingIndex >= 0) {
          const updatedStats = [...state.eomModuleStats];
          updatedStats[existingIndex] = newStats;
          return { eomModuleStats: updatedStats };
        }

        return { eomModuleStats: [...state.eomModuleStats, newStats] };
      },
      false,
      'stageSlice/setEomModuleStats',
    ),
  setExplanationStats: (newStat: ExplanationStats) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          if (state.currentDifficultyQuestionStats.length > 0) {
            const lastIndex = state.currentDifficultyQuestionStats.length - 1;
            if (state.currentDifficultyQuestionStats[lastIndex]) {
              const existingExplanationStats =
                state.currentDifficultyQuestionStats[lastIndex].explanationStats || [];
              const existingIndex = existingExplanationStats.findIndex(
                (explanation) =>
                  explanation.explanationIndex === newStat.explanationIndex &&
                  explanation.type === newStat.type,
              );

              if (existingIndex >= 0) {
                const existingExplanation = existingExplanationStats[existingIndex];
                existingExplanationStats[existingIndex] = {
                  ...newStat,
                  metadata: {
                    ...newStat.metadata,
                    viewTime: existingExplanation.metadata.viewTime + newStat.metadata.viewTime,
                    viewCount: existingExplanation.metadata.viewCount + 1,
                    ...(newStat.metadata.pauseTime && {
                      pauseTime:
                        (existingExplanation.metadata.pauseTime || 0) + newStat.metadata.pauseTime,
                    }),
                    ...(newStat.metadata.completion && {
                      completion: Math.max(
                        existingExplanation.metadata.completion || 0,
                        newStat.metadata.completion,
                      ),
                    }),
                    ...(newStat.metadata.pageViewCount && {
                      pageViewCount: Math.max(
                        existingExplanation.metadata.pageViewCount || 0,
                        newStat.metadata.pageViewCount,
                      ),
                    }),
                    ...(newStat.metadata.totalPageCount && {
                      totalPageCount: newStat.metadata.totalPageCount,
                    }),
                  },
                };
              } else {
                existingExplanationStats.push({
                  ...newStat,
                  metadata: {
                    ...newStat.metadata,
                    viewCount: 1,
                  },
                });
              }

              state.currentDifficultyQuestionStats[lastIndex].explanationStats =
                existingExplanationStats;
            }
          }
        }),
      false,
      'stageSlice/setExplanationStats',
    ),
  setHintStats: (newStat: HintStats) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const lastIndex = state.currentDifficultyQuestionStats.length - 1;
          if (state.currentDifficultyQuestionStats[lastIndex]) {
            const existingHintStats =
              state.currentDifficultyQuestionStats[lastIndex].hintStats || [];
            const existingIndex = existingHintStats.findIndex(
              (hint) => hint.hintIndex === newStat.hintIndex && hint.type === newStat.type,
            );

            if (existingIndex >= 0) {
              const existingHint = existingHintStats[existingIndex];
              existingHintStats[existingIndex] = {
                ...newStat,
                viewTime: existingHint.viewTime + newStat.viewTime,
                viewCount: existingHint.viewCount + 1,
              };
            } else {
              existingHintStats.push({
                ...newStat,
                viewCount: 1,
              });
            }

            state.currentDifficultyQuestionStats[lastIndex].hintStats = existingHintStats;
          }
        }),
      false,
      'stageSlice/setHintStats',
    ),
  setPreQuestionExplanationStats: (newStat: PreQuestionExplanationStats) =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const existing = state.currentPreQuestionExplanationStats;
          state.currentPreQuestionExplanationStats = {
            type: newStat.type,
            viewCount: (existing?.viewCount ?? 0) + newStat.viewCount,
            viewTime: (existing?.viewTime ?? 0) + newStat.viewTime,
          };
        }),
      false,
      'stageSlice/setPreQuestionExplanationStats',
    ),

  resetCurrentNarrationStatsCueing: () =>
    set(
      { currentNarrationStats: { completed: false, playCount: 0 } },
      false,
      'stageSlice/resetCurrentNarrationStatsCueing',
    ),
  reportNarrationReached90Cueing: () =>
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const newPlayCount = state.currentNarrationStats.playCount + 1;
          state.currentNarrationStats = { completed: true, playCount: newPlayCount };
          const lastIndex = state.currentDifficultyQuestionStats.length - 1;
          if (
            lastIndex >= 0 &&
            state.currentDifficultyQuestionStats[lastIndex]?.narration?.enabled
          ) {
            state.currentDifficultyQuestionStats[lastIndex].narration = {
              enabled: true,
              completed: true,
              playCount: newPlayCount,
            };
          }
        }),
      false,
      'stageSlice/reportNarrationReached90Cueing',
    ),

  /** stage journey related behaviour */
  setShowStageTask: (showStageTask: boolean) =>
    set({ showStageTask }, false, 'stageSlice/setShowStageTask'),
  clearStageState: () => set({ ...getInitialState() }, false, 'stageSlice/clearStageState'),

  clearStageTaskResult: () =>
    set({ stageTaskResult: undefined }, false, 'stageSlice/clearStageTaskResult'),
  clearSharedState: () => {
    set({ ...getSharedInitialState() }, false, 'stageSlice/clearSharedState');
  },

  /** api execution related behaviour */
  incrementAddStatsRetryCount: () =>
    set(
      (state) => ({
        addStatsRetryCount: state.addStatsRetryCount + 1,
      }),
      false,
      'stageSlice/incrementAddStatsRetryCount',
    ),
  incrementSubmitResultRetryCount: () =>
    set(
      (state) => ({
        submitResultRetryCount: state.submitResultRetryCount + 1,
      }),
      false,
      'stageSlice/incrementSubmitResultRetryCount',
    ),
  getSubmitResultRetryCount: () => get().submitResultRetryCount,
  getAddStatsRetryCount: () => get().addStatsRetryCount,

  setCurrentModuleIndex: (currentModuleIndex: number) =>
    set({ currentModuleIndex }, false, 'stageSlice/setCurrentModuleIndex'),

  processUserAnswer: (source: string) => {
    let resultDone = false;
    set(
      (_state) =>
        produce(_state, (state: StageState) => {
          const {
            overallQuestionStats,
            currentDifficultyQuestionStats,
            isEvaluatingPivot,
            difficultyWiseQuestions,
            getCurrentDifficulty,
            maxDifficulty,
            getAvailableQuestions,
            minDifficulty,
            getQuestionsResponse,
            stageTaskResult,
          } = state;
          if (stageTaskResult) return;
          if (!getQuestionsResponse) return;
          const isFlnMode = get().currentTheme === 'fln' && !isEvaluatingPivot;
          const passThreshold = getPassThreshold(isFlnMode);
          const currentDifficulty = getCurrentDifficulty();
          const availableQuestions = getAvailableQuestions();
          const { currentSetOver, score } = isCurrentSetOver(
            currentDifficultyQuestionStats,
            availableQuestions,
            isFlnMode,
          );

          if (currentSetOver) {
            state.overallQuestionStats = [
              ...overallQuestionStats,
              ...currentDifficultyQuestionStats,
            ];
            state.currentSet += 1;
            state.currentDifficultyQuestionStats = [];
            if (isEvaluatingPivot) {
              const { pivotQuestions } = getQuestionsResponse;
              state.stageTaskResult = {
                source,
                status:
                  score >= passThreshold || pivotQuestions.length < MIN_QUESTIONS_FOR_FAIL
                    ? 'PASS'
                    : 'FAIL',
                lastDifficulty: maxDifficulty,
              };
              state.question = undefined;
              resultDone = true;
            } else if (
              score < passThreshold &&
              currentDifficulty &&
              difficultyWiseQuestions[currentDifficulty].length >= MIN_QUESTIONS_FOR_FAIL
            ) {
              state.stageTaskResult = {
                source,
                status: 'FAIL',
                lastDifficulty:
                  currentDifficulty && currentDifficulty > minDifficulty
                    ? currentDifficulty - 1
                    : 0,
              };
              state.question = undefined;
              resultDone = true;
            } else {
              const continueFormative =
                currentDifficulty &&
                currentDifficulty < maxDifficulty &&
                difficultyWiseQuestions[currentDifficulty + 1];
              if (continueFormative) {
                state.isEvaluatingPivot = false;
                state.question = selectRandomQuestion(
                  difficultyWiseQuestions[currentDifficulty + 1],
                );
              } else {
                state.isEvaluatingPivot = true;
                state.question = undefined;
              }
            }
          } else {
            state.question = selectRandomQuestion(availableQuestions);
          }
          state.currentNarrationStats = { completed: false, playCount: 0 };
          state.translationViewed = getInititalTranslationViewed(
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.question,
            // eslint-disable-next-line unicorn/consistent-destructuring
            getQuestionsResponse?.mediumPriority,
          );
          state.currentQuestionStat = undefined;
          state.currentPreQuestionExplanationStats = undefined;
        }),
      false,
      'stageSlice/processUserAnswer',
    );
    return resultDone;
  },
  getCurrentDifficulty: () => get().question?.difficulty,
  getAvailableQuestions: () => {
    const {
      getQuestionsResponse,
      difficultyWiseQuestions,
      getCurrentDifficulty,
      isEvaluatingPivot,
    } = get();
    if (getQuestionsResponse) {
      const { pivotQuestions } = getQuestionsResponse;
      const currentDifficulty = getCurrentDifficulty();
      if (currentDifficulty) {
        const questions = isEvaluatingPivot
          ? pivotQuestions
          : difficultyWiseQuestions[currentDifficulty];
        const attemptedQuestionIds = new Set(
          [...get().currentDifficultyQuestionStats, ...get().overallQuestionStats].map(
            (stat) => stat.questionId,
          ),
        );
        return questions?.filter((q) => !attemptedQuestionIds.has(q.id)) || [];
      }
    }
    return [];
  },
  getCueingEngineResult: () => {
    const {
      selectedSubject,
      currentStage,
      getStatsResponse,
      overallQuestionStats,
      eomModuleStats,
      explanationStats,
      hintStats,
      stageTaskResult,
      missionAttemptNo,
      cueingAttemptId,
      maxDifficulty,
      isEvaluatingPivot,
    } = get();
    let finalResult;
    if (getStatsResponse && currentStage && selectedSubject && missionAttemptNo) {
      const { stageAttemptNo, engagementStrategyId: strategyId } =
        getStatsResponse.studentStageDetails;
      const attemptId = cueingAttemptId ?? uuidv4();
      const isFormativePass = !stageTaskResult && isEvaluatingPivot;
      finalResult = {
        stageId: currentStage.id,
        status: isFormativePass ? 'FORMATIVE_PASS' : stageTaskResult?.status ?? 'PASS',
        subjectId: selectedSubject.id,
        stageAttemptNo,
        missionAttemptNo,
        strategyId,
        lastDifficulty: stageTaskResult?.lastDifficulty ?? maxDifficulty,
        eomModuleStats,
        ...(explanationStats.length > 0 && { explanationStats }),
        ...(hintStats.length > 0 && { hintStats }),
        questionStats: { attemptId, stats: overallQuestionStats },
      };
    }
    return finalResult;
  },
});
